import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {
    Flex,
    Button,
    Input,
    useMultiStyleConfig,
    Text,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import useProductRegistrationFields from './useProductRegistrationFields'
import {FormattedMessage} from 'react-intl'
import Field from '../../../../components/field'
import {CalendarIcon} from '../../../../components/icons/others'
import {validateFile} from '../../../../utils/upload-utils'
import {BRAND_KEY} from '../../../../brand'

const ProductRegistrationFields = ({
    form,
    prefix = '',
    setReceiptBase64,
    setReceiptName,
    setReceiptMime,
    setReceiptFile,
    initialFile,
    disableSerialField,
    isEditForm,
    fileError,
    setFileError, // Accept setFileError as a prop
    isGuest,
    isAccount = false,
}) => {
    const fields = useProductRegistrationFields({form, prefix})
    const formRef = useRef()
    const formStyles = useMultiStyleConfig('ProductRegistrationForm')
    // create three formatted messages for the file name, error message and the upload file button
    const uploadFileLabel = (
        <FormattedMessage
            id="product_registration.upload_file"
            defaultMessage="Upload file (Max 5 MB)*"
        />
    )
    const uploadFileError = (
        <FormattedMessage
            id="product_registration.upload_file_error"
            defaultMessage="Please upload a file."
        />
    )
    const uploadFileSizeError = (
        <FormattedMessage
            id="product_registration.upload_file_size_error"
            defaultMessage="File size exceeds 5 MB."
        />
    )
    const [fileName, setFileName] = useState(uploadFileLabel)
    const [errorMessage, setErrorMessage] = useState('')

    useEffect(() => {
        // Focus on the form when the component mounts for accessibility
        formRef?.current?.focus()
    }, [])

    useEffect(() => {
        if (initialFile) {
            setFileName(initialFile.name)
            setReceiptName(initialFile.name)
            setReceiptMime(initialFile.type)
        }
    }, [initialFile])

    const handleFileChange = async (e) => {
        const file = e.target.files[0]
        if (!file) {
            setErrorMessage(uploadFileError)
            //setFileError('Please upload a file.') // Set fileError in parent
            return
        }

        if (file.size > 5 * 1024 * 1024) {
            setErrorMessage(uploadFileSizeError)
            //setFileError('File size exceeds 5 MB.') // Set fileError in parent
            return
        }

        const isValid = await validateFile(file)

        if (!isValid) {
            setErrorMessage(
                <FormattedMessage
                    id="contact_us.file_type_error"
                    defaultMessage="Only PDF/JPEG/PNG files are allowed"
                />,
            )
            return
        }

        setErrorMessage('')
        setFileError('') // Clear fileError in parent
        setReceiptName(file.name)
        setReceiptMime(file.type)
        setFileName(file.name)
        setReceiptFile(file) // Pass the file object itself

        // Create a FileReader to convert file to base64
        const reader = new FileReader()
        reader.onloadend = () => {
            // The result is a Data URL: "data:<mime type>;base64,<encoded data>"
            // Split at the comma to remove the "data:" prefix
            const base64String = reader.result.split(',')[1]
            setReceiptBase64(base64String)
        }

        // Read the entire file as a Data URL
        reader.readAsDataURL(file)
    }

    return (
        <Flex {...formStyles.formInnerContainer}>
            <Field
                {...fields.purchasedate}
                type="date"
                clearIcon={isAccount}
                calendarIcon={<CalendarIcon />}
                {...form.register(fields.purchasedate.name, fields.purchasedate.rules)}
                isEditForm={isEditForm}
                isDisabled={isAccount}
            />
            <Flex {...formStyles.uploadFileContainer} align="flex-start" gap={2}>
                {/* File Select Button */}
                <Button
                    {...formStyles.fileButton}
                    variant={'primary'}
                    isDisabled={isGuest}
                    onClick={() => document.getElementById(fields.uploadFile.name).click()}
                >
                    {fields.uploadFile.label}
                </Button>

                {/* File name "input" and error message */}
                <Flex direction="column" flex={1}>
                    {/* Styled "input-like" box */}
                    <Flex
                        {...formStyles.fileNameContainer}
                        borderColor={(fileError || errorMessage) && 'red'}
                    >
                        <Text {...formStyles.fileNameLabel}>{errorMessage || fileName}</Text>
                    </Flex>

                    {/* Error message */}
                    {fileError && (
                        <Text fontSize={10} color="text.danger" mt={1}>
                            {fileError}
                        </Text>
                    )}
                </Flex>
                <Input
                    type="file"
                    id={fields.uploadFile.name}
                    {...fields.uploadFile}
                    onChange={handleFileChange}
                    style={{display: 'none'}}
                />
            </Flex>
            <Field
                {...fields.source}
                isDisabled={isGuest}
                placeholder={
                    <FormattedMessage
                        id="product_registration.where_did_you_buy"
                        defaultMessage="Where did you make the purchase?"
                    />
                }
            />
            <Flex {...formStyles.skuNumberContainer}>
                <Field {...fields.serialNumber} isDisabled={disableSerialField || isGuest} />
                {BRAND_KEY !== 'nutribullet' && (
                    <Text {...formStyles.linkText}>
                        <FormattedMessage
                            id="product_registration_fields.serial_number_link"
                            defaultMessage="<u>Where can I find the machine's serial number?</u>"
                            values={{
                                u: (chunks) => (
                                    <u>
                                        <a
                                            href="https://www.delonghi.com/it-it/support/find-serial-number"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {chunks}
                                        </a>
                                    </u>
                                ),
                            }}
                        />
                    </Text>
                )}
            </Flex>
        </Flex>
    )
}

ProductRegistrationFields.propTypes = {
    form: PropTypes.object.isRequired,
    prefix: PropTypes.string,
    disableSerialField: PropTypes.bool,
    isEditForm: PropTypes.bool,
    formTitleAriaLabel: PropTypes.object,
    setReceiptBase64: PropTypes.func.isRequired,
    setReceiptName: PropTypes.func.isRequired,
    setReceiptMime: PropTypes.func.isRequired,
    setReceiptFile: PropTypes.func.isRequired,
    initialFile: PropTypes.object,
    fileError: PropTypes.string,
    setFileError: PropTypes.func.isRequired,
    isGuest: PropTypes.bool,
    isAccount: PropTypes.bool,
}

export default ProductRegistrationFields
